import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, useQuery, gql } from '@apollo/client'
import { createMessageOfTheDay, getMessageOfTheDay } from '../../../apollo'
import { Box, Typography, Input, Button, Alert } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const SAVE_MESSAGE_OF_THE_DAY = gql`
  ${createMessageOfTheDay}
`
const GET_MESSAGE_OF_THE_DAY = gql`
  ${getMessageOfTheDay}
`

function MessageOfTheDayConfiguration(props) {
  const { t } = props
  const restaurantId =
    props.restaurantId || localStorage.getItem('restaurantId') || ''
  const [message, setMessage] = useState('')

  const [mutate, { loading }] = useMutation(SAVE_MESSAGE_OF_THE_DAY)
  const { data: messageData, refetch } = useQuery(GET_MESSAGE_OF_THE_DAY, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (typeof messageData?.getMessageOfTheDay === 'string') {
      setMessage(messageData.getMessageOfTheDay)
    }
  }, [messageData])

  const handleSuccess = message => {
    setSuccessMessage(message)
  }

  const handleError = error => {
    setErrorMessage('An error occurred while saving configuration.')
    console.error('Mutation error:', error)
  }

  useEffect(() => {
    if (successMessage) {
      const timeoutId = setTimeout(() => {
        setSuccessMessage('')
      }, 3000)
      return () => clearTimeout(timeoutId)
    }
  }, [successMessage])

  useEffect(() => {
    if (errorMessage) {
      const timeoutId = setTimeout(() => {
        setErrorMessage('')
      }, 3000)
      return () => clearTimeout(timeoutId)
    }
  }, [errorMessage])

  const onSave = e => {
    e.preventDefault()
    if (!loading) {
      mutate({
        variables: {
          message,
          restaurantId
        },
        onCompleted: data => {
          const savedMessage = data?.createMessageOfTheDay?.messageoftheDay
          if (typeof savedMessage === 'string') {
            setMessage(savedMessage)
          }
          refetch()
          handleSuccess(t('Message saved successfully!'))
        },
        onError: error => {
          handleError(error)
        }
      })
    }
  }

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('Message of the Day')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Box mt={2}>
            <Typography className={classes.labelText}>
              {t('Message of the Day')}
            </Typography>
            <Typography
              variant="caption"
              style={{ color: '#666', marginBottom: 8, display: 'block' }}>
              {t('Important announcement shown to users')}
            </Typography>
            <Input
              id="input-message-of-the-day"
              name="input-message-of-the-day"
              placeholder={t('Enter message')}
              multiline
              rows={4}
              value={message}
              onChange={event => setMessage(event.target.value)}
              disableUnderline
              style={{
                marginTop: -1,
                height: 'auto',
                backgroundColor: '#fafafa'
              }}
              className={globalClasses.input}
            />
          </Box>
          <Box mt={2}>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={onSave}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(MessageOfTheDayConfiguration)
